import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { monthKey, monthLabel, shiftMonth } from "@/lib/format";
import { toast } from "sonner";
import { KpiCards } from "@/components/KpiCards";
import { NextCommission } from "@/components/NextCommission";
import { StreakBestDay } from "@/components/StreakBestDay";
import { WorkCalendar } from "@/components/WorkCalendar";
import { EarningsChart } from "@/components/EarningsChart";
import { SalesTable } from "@/components/SalesTable";
import { AddSaleDialog } from "@/components/AddSaleDialog";
import { SundayBonusDialog } from "@/components/SundayBonusDialog";
import { ChevronLeft, ChevronRight, LogOut, Wallet } from "lucide-react";

export default function Dashboard() {
  const nav = useNavigate();
  const [month, setMonth] = useState(monthKey());
  const [dash, setDash] = useState(null);
  const [sales, setSales] = useState([]);
  const [workdays, setWorkdays] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sundayPrompt, setSundayPrompt] = useState(null); // { date }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, s, w] = await Promise.all([
        api.dashboard(month),
        api.listSales(month),
        api.listWorkdays(month),
      ]);
      setDash(d);
      setSales(s);
      setWorkdays(w);
    } catch (e) {
      // 401 auto-redirects via axios interceptor
      if (e?.response?.status !== 401) console.warn("Dashboard load failed:", e);
    }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const dailyMap = useMemo(() => {
    const map = {};
    for (const d of dash?.daily || []) map[d.date] = d;
    return map;
  }, [dash]);

  const onSave = async (form) => {
    if (editing) {
      await api.updateSale(editing.id, form);
      toast.success("Sale updated");
    } else {
      await api.createSale(form);
      toast.success("Sale added");
    }
    setEditing(null);
    load();
  };

  const onDelete = async (s) => {
    if (!window.confirm(`Delete sale from ${s.customer_name}?`)) return;
    await api.deleteSale(s.id);
    toast.success("Deleted");
    load();
  };

  const onToggleWork = async (date, isSunday = false) => {
    // If it's a Sunday and NOT already marked worked, ask about the $100 bonus first.
    const alreadyWorked = workdays.some((w) => w.work_date === date);
    if (isSunday && !alreadyWorked) {
      setSundayPrompt({ date });
      return;
    }
    try {
      const r = await api.toggleWorkday(date, false);
      toast.success(r.worked ? `+$${dash?.base_pay_per_day || 170} base pay` : "Removed");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const confirmSunday = async (withBonus) => {
    const date = sundayPrompt?.date;
    setSundayPrompt(null);
    if (!date) return;
    try {
      const r = await api.toggleWorkday(date, withBonus);
      const extra = withBonus ? " +$100 Sunday bonus" : "";
      toast.success(`+$${dash?.base_pay_per_day || 170} base pay${extra}`);
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const logout = async () => {
    try { await api.logout(); } catch (e) { console.warn("Logout error:", e); }
    localStorage.removeItem("session_token");
    nav("/");
  };

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <header className="glass-strong px-4 py-3 flex items-center justify-between mb-6 sticky top-4 z-30">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-2xl glass-pill flex items-center justify-center flex-shrink-0">
            <Wallet className="w-5 h-5 text-sky-300" strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-white/50 leading-none">Income Tracker</p>
            <h1 className="font-heading text-base sm:text-lg font-bold text-white leading-tight mt-1 truncate">
              {monthLabel(month)}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={() => setMonth(shiftMonth(month, -1))}
            className="glass-pill w-9 h-9 inline-flex items-center justify-center text-white/80 active:opacity-70"
            data-testid="prev-month-btn"
            aria-label="Previous month"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setMonth(monthKey())}
            className="hidden sm:inline-flex items-center justify-center glass-pill h-9 px-4 text-xs font-semibold text-white active:opacity-70 leading-none"
            data-testid="today-month-btn"
          >
            Today
          </button>
          <button
            onClick={() => setMonth(shiftMonth(month, 1))}
            className="glass-pill w-9 h-9 inline-flex items-center justify-center text-white/80 active:opacity-70"
            data-testid="next-month-btn"
            aria-label="Next month"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={logout}
            className="glass-pill w-9 h-9 inline-flex items-center justify-center text-white/60 active:opacity-70 ml-1"
            data-testid="logout-btn"
            aria-label="Lock"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {loading && !dash ? (
        <div className="glass p-10 text-center text-white/60">Loading…</div>
      ) : (
        <div className="space-y-5">
          <KpiCards data={dash} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <NextCommission data={dash} />
            <StreakBestDay data={dash} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2">
              <EarningsChart daily={dash?.daily} />
            </div>
            <WorkCalendar
              monthKeyStr={month}
              workdays={workdays}
              onToggle={onToggleWork}
              dailyMap={dailyMap}
              basePay={dash?.base_pay_per_day || 170}
            />
          </div>

          <SalesTable
            sales={sales}
            rate={dash?.commission_rate || 0.1}
            onAdd={() => { setEditing(null); setDialogOpen(true); }}
            onEdit={(s) => { setEditing(s); setDialogOpen(true); }}
            onDelete={onDelete}
          />
        </div>
      )}

      <AddSaleDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSave={onSave}
        initial={editing}
      />
      <SundayBonusDialog
        open={!!sundayPrompt}
        onClose={() => setSundayPrompt(null)}
        onDecide={confirmSunday}
      />
    </div>
  );
}
